import * as React from 'react';
import {useMemo, useState} from 'react';
import {PcPage} from "@/component/layout/PcPage";
import {useParams} from "react-router-dom";
import {LoadableView} from "@/component/view/LoadableView";
import {FtDate} from "@/common/util/FtDate";
import {TransDto} from "@/common/dto/TransDto";
import {useDialog} from "@/dialog/DialogContext";
import {useAccountStore} from "@/store/useAccountStore";
import {useTransactionAccountSummary, useTransactions} from "@/hook/useTransactions";
import {TransAccount} from "@/common/dto/TransAccount";
import {Money} from "@/component/style/Money";
import {NumberTable} from "@/component/style/NumberTable";
import {FtButton} from "@/common/FtButton";
import {ErrorUtil} from "@/utils/ErrorUtil";

interface TransInputItem {
    date: string;
    amount: number;
    detail: string;
}

interface CompareResultItem {
    date: string;
    amount: number;
    type: 'MATCH' | 'ONLY_DB' | 'ONLY_INPUT' | 'OUT_OF_RANGE';
    dbTrans?: TransDto;
    inputTrans?: TransInputItem;
}

const compareResultTypeLabels: Record<CompareResultItem['type'], string> = {
    'MATCH': '일치',
    'ONLY_DB': '오입력',
    'ONLY_INPUT': '누락',
    'OUT_OF_RANGE': '이미 마감됨',
}

const compareResultTypeColors: Record<CompareResultItem['type'], string> = {
    'MATCH': 'gray',
    'ONLY_DB': 'red',
    'ONLY_INPUT': 'blue',
    'OUT_OF_RANGE': 'orange',
}

export function CloseCardPage() {
    const params = useParams();
    const accountId = parseInt(params.accountId as string);

    const [inputText, setInputText] = useState<string>("");
    const account = useAccountStore(state => state.getAccountById(accountId));
    const inputItems = useMemo(() => parseInputText(inputText), [inputText]);

    return <div>
        <textarea value={inputText} onChange={e => setInputText(e.target.value)} rows={5} style={{width: '100%'}}/>
        <div style={{marginBottom: 16, color: 'gray'}}>
            형식: 날짜 (yy.mm.dd), 금액, 내용
        </div>

        {account && inputItems.length > 0 && (
            <CompareView account={account} inputItems={inputItems}/>
        )}
    </div>;
}

function CompareView(props: {
    inputItems: TransInputItem[];
    account: TransAccount;
}) {
    const {inputItems, account} = props;
    const startDate = account!.outLockDate ? new FtDate(account!.outLockDate).addDay(1).ymd() : undefined;
    const endDate = inputItems[inputItems.length - 1].date;

    const {data: transactions, error} = useTransactions({
        outAccountId: account!.id,
        transactDateFrom: startDate,
        transactDateTo: endDate,
    });
    const {data: inTransactions} = useTransactions({inAccountId: account!.id, transactDateFrom: endDate,});
    const {data: balance} = useTransactionAccountSummary(account.id, undefined, endDate);

    const [isClosing, setIsClosing] = useState(false);

    const dialog = useDialog();
    const closeAccount = useAccountStore(state => state.closeAccount);

    const compareResult: CompareResultItem[] | undefined = useMemo(() => {
        if (transactions === undefined || !account || inputItems.length === 0) return undefined;

        transactions.sort((a, b) => {
            if (a.transactDate !== b.transactDate) return a.transactDate.localeCompare(b.transactDate);
            else return a.amount - b.amount;
        });
        return compareTrans(transactions, inputItems, startDate ?? '0000-00-00');
    }, [transactions, inputItems]);

    async function closeAction(inLockDate: string) {
        setIsClosing(true);
        try {
            await closeAccount(account.id, {inLockDate, outLockDate: endDate});
        } catch (e) {
            alert(ErrorUtil.getErrorMessage(e));
        }
        setIsClosing(false);
    }

    return <LoadableView data={compareResult} error={error} component={(items) => (<div>

        <h3>사용 내역 비교 결과</h3>
        <NumberTable>
            <thead>
            <tr>
                <th>날짜</th>
                <th>금액</th>
                <th>상태</th>
                <th>내용</th>
                <th>작업</th>
            </tr>
            </thead>
            <tbody>
            {items.map((item, index) => (
                <tr key={index}
                    style={{textAlign: "center", color: compareResultTypeColors[item.type]}}>
                    <td>{item.date}</td>
                    <td><Money amount={item.amount}/></td>
                    <td>{compareResultTypeLabels[item.type]}</td>
                    <td>{item.dbTrans ? item.dbTrans.detail : ''} {item.inputTrans ? item.inputTrans.detail : ''}</td>
                    <td>
                        {item.type === 'ONLY_INPUT' && (
                            <button onClick={async () => {
                                dialog.openDialog('addTrans', {
                                    outAccountId: account.id,
                                    transactDate: item.inputTrans?.date,
                                    amount: item.amount,
                                    detail: item.inputTrans?.detail,
                                });
                            }}>등록</button>
                        )}

                        {item.type === 'ONLY_DB' && item.dbTrans && (
                            <button onClick={async () => {
                                dialog.openDialog('editTrans', {
                                    transId: item.dbTrans!.id,
                                });
                            }}>수정</button>
                        )}
                    </td>
                </tr>
            ))}
            </tbody>
        </NumberTable>

        {balance && (
            <div style={{marginTop: '10px'}}>
                {endDate} 잔액: <Money amount={account.startAmount + balance.inSum - balance.outSum}/>원
            </div>
        )}

        {inTransactions && balance && (
            <div style={{marginTop: '10px'}}>
                <h3>{endDate} 이후 입금 내역</h3>
                <ul>
                    {inTransactions.map(t => (<li key={t.id}>
                            {t.transactDate} <Money amount={t.amount}/> {t.detail}

                            {-t.amount === account.startAmount + balance.inSum - balance.outSum && t.transactDate > (account.inLockDate || '0000-00-00') && (
                                <FtButton style={{marginLeft: 16, minWidth: 100}}
                                          disabled={isClosing}
                                          onClick={() => closeAction(t.transactDate)}>
                                    {isClosing ? "마감중..." : "마감"}
                                </FtButton>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        )}
    </div>)}/>
}

function normalizeDate(dateStr: string): string {
    // yy.mm.dd
    if (/^\d{2}\.\d{1,2}\.\d{1,2}$/.test(dateStr)) {
        const parts = dateStr.split('.');
        const year = parseInt(parts[0]) + 2000;
        const month = parts[1].padStart(2, '0');
        const day = parts[2].padStart(2, '0');
        return `${year}-${month}-${day}`;
    } else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateStr)) {
        return dateStr;
    } else {
        throw new Error(`Invalid date format: ${dateStr}`);
    }
}

function parseInputText(text: string): TransInputItem[] {
    const lines = text.split('\n');
    const result: TransInputItem[] = [];

    for (const line of lines) {
        const parts = line.split("\t");
        if (parts.length >= 3) {
            try {
                const date = normalizeDate(parts[0].trim())
                const amount = parseInt(parts[1].replace(/[, ]/g, ''));
                const detail = parts[2].trim();

                if (!isNaN(amount)) {
                    result.push({date, amount, detail});
                }
            } catch (e) {
                console.info('Skipping unparseable line: ' + line);
            }
        }
    }

    result.sort((a, b) => {
        if (a.date !== b.date) {
            return a.date.localeCompare(b.date);
        } else {
            return a.amount - b.amount;
        }
    });

    return result;
}

function compareTrans(a: TransDto[], b: TransInputItem[], startDate: string): CompareResultItem[] {
    const result: CompareResultItem[] = [];

    const leftA = a.slice();
    const leftB = b.slice();

    // 먼저 B 기준으로 정확히 매치되는 것 찾기
    for (let i = leftB.length - 1; i >= 0; i--) {
        if (leftB[i].date < startDate) {
            result.push({type: 'OUT_OF_RANGE', inputTrans: leftB[i], date: leftB[i].date, amount: leftB[i].amount});
            leftB.splice(i, 1);
            continue;
        }

        const bItem = leftB[i];
        const matchIndex = leftA.findIndex(aItem => aItem.transactDate === bItem.date && aItem.amount === bItem.amount);
        if (matchIndex !== -1) {
            result.push({
                type: 'MATCH',
                dbTrans: leftA[matchIndex],
                inputTrans: bItem,
                date: bItem.date,
                amount: bItem.amount
            });
            leftA.splice(matchIndex, 1);
            leftB.splice(i, 1);
        }
    }

    // 남은 A, B 각각 처리
    for (const aItem of leftA) {
        result.push({type: 'ONLY_DB', dbTrans: aItem, date: aItem.transactDate, amount: aItem.amount});
    }
    for (const bItem of leftB) {
        result.push({type: 'ONLY_INPUT', inputTrans: bItem, date: bItem.date, amount: bItem.amount});
    }

    result.sort((x, y) => {
        if (x.date !== y.date) return x.date.localeCompare(y.date);
        else return x.amount - y.amount;
    });

    return result;
}